import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Clock, Bell } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Home = () => {
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        await axios.post(`${API}/seed`);
        const productsRes = await axios.get(`${API}/products`);
        if (productsRes.data.length > 0) {
          setProduct(productsRes.data[0]);
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <div className="min-h-screen bg-[#F5F2E8]">
      {/* Hero Section - Latest Drop */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden bg-[#1A1A1A]">
        <div className="absolute inset-0 data-grid opacity-30"></div>
        
        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-16">
          {/* Drop Alert Banner */}
          <div className="flex justify-center mb-8">
            <div className="inline-flex items-center gap-3 bg-[#4A7C29] text-white px-5 py-2">
              <Clock className="w-4 h-4" />
              <span className="text-xs font-semibold tracking-[0.15em] font-['IBM_Plex_Sans_Condensed'] uppercase">PRODUCT RELEASE 001</span>
              <span className="bg-[#1A1A1A] px-2 py-0.5 text-[10px] tracking-wider">INVENTORY DEPLETED</span>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Product Image */}
            <div className="relative">
              {loading ? (
                <div className="aspect-square bg-[#2C2C2C] skeleton"></div>
              ) : product ? (
                <div className="relative">
                  <div className="aspect-square overflow-hidden bg-[#2C2C2C] border-2 border-[#404040]">
                    <img 
                      src={product.image_url} 
                      alt={product.name}
                      className="w-full h-full object-cover opacity-90"
                    />
                  </div>
                  {/* Sold Out Overlay */}
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                    <div className="bg-[#1A1A1A] text-white px-8 py-4 border-2 border-[#4A7C29] transform -rotate-6">
                      <span className="text-xl font-semibold tracking-[0.2em] font-['IBM_Plex_Sans_Condensed'] uppercase">SOLD OUT</span>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>

            {/* Product Info */}
            <div className="text-white">
              <p className="text-[#4A7C29] text-[10px] font-semibold tracking-[0.2em] mb-3 font-['IBM_Plex_Sans_Condensed'] uppercase">TJGI — ADVANCED MATERIALS DIVISION</p>
              <h1 className="text-3xl md:text-4xl font-semibold tracking-[0.05em] mb-4 font-['IBM_Plex_Sans_Condensed'] uppercase">
                {product?.name || 'Loading...'}
              </h1>
              <p className="text-[#6B6B6B] text-base mb-6 leading-relaxed font-['IBM_Plex_Sans']">
                {product?.description || ''}
              </p>
              
              <div className="flex items-center gap-6 mb-8">
                <span className="text-2xl font-semibold font-['IBM_Plex_Sans']">${product?.price?.toFixed(2) || '0.00'}</span>
                <span className="text-[#6B6B6B] bg-[#2C2C2C] px-3 py-1 font-['IBM_Plex_Mono'] text-sm border border-[#404040]">
                  {product?.weight || ''}
                </span>
              </div>

              {/* Sold Out State */}
              <div className="bg-[#2C2C2C] border-l-4 border-[#4A7C29] p-6 mb-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-2 h-2 bg-[#4A7C29]"></div>
                  <span className="text-white font-semibold text-sm font-['IBM_Plex_Sans_Condensed'] uppercase tracking-wide">STATUS: INVENTORY DEPLETED</span>
                </div>
                <p className="text-[#6B6B6B] text-sm font-['IBM_Plex_Sans']">
                  This release has concluded. Register below for notification of future product availability.
                </p>
              </div>

              <Link to={product ? `/products/${product.id}` : '#'}>
                <Button 
                  variant="outline"
                  className="rounded-none border-2 border-[#404040] text-white font-semibold px-6 py-5 hover:bg-[#2C2C2C] hover:border-[#4A7C29] transition-colors font-['IBM_Plex_Sans_Condensed'] uppercase tracking-wider text-xs"
                  data-testid="view-product-btn"
                >
                  VIEW PRODUCT SPECIFICATIONS
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Drop Notification Signup */}
      <section className="py-12 bg-[#4A7C29]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-white/20 flex items-center justify-center">
                <Bell className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white font-['IBM_Plex_Sans_Condensed'] uppercase tracking-wide">FUTURE RELEASE NOTIFICATIONS</h3>
                <p className="text-white/80 text-sm font-['IBM_Plex_Sans']">Automated alert system for product availability.</p>
              </div>
            </div>
            <Link to="/#notify">
              <Button 
                className="rounded-none bg-white text-[#4A7C29] font-semibold px-8 py-4 hover:bg-[#F5F2E8] transition-colors font-['IBM_Plex_Sans_Condensed'] uppercase tracking-wider text-xs"
                onClick={() => {
                  const footer = document.querySelector('footer');
                  footer?.scrollIntoView({ behavior: 'smooth' });
                }}
                data-testid="notify-btn"
              >
                REGISTER FOR ALERTS
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-24 bg-[#1A1A1A] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-[#4A7C29] text-[10px] font-semibold tracking-[0.2em] mb-4 font-['IBM_Plex_Sans_Condensed'] uppercase">CORPORATE OVERVIEW</p>
          <h2 className="text-2xl md:text-3xl font-semibold tracking-[0.05em] mb-6 font-['IBM_Plex_Sans_Condensed'] uppercase">
            TASTYJELLY GLOBAL INDUSTRIES
          </h2>
          <p className="text-[#6B6B6B] text-base mb-8 max-w-2xl mx-auto leading-relaxed font-['IBM_Plex_Sans']">
            We apply the authority of a global research institution to problems that could have been solved without it. Limited quantity ultralight gear. When inventory is depleted, production cycles conclude.
          </p>
          <Link to="/about">
            <Button 
              variant="outline"
              className="rounded-none border-2 border-[#404040] text-white font-semibold px-8 py-5 hover:bg-[#2C2C2C] hover:border-[#4A7C29] transition-colors font-['IBM_Plex_Sans_Condensed'] uppercase tracking-wider text-xs"
            >
              INSTITUTIONAL HISTORY
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
